import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { DOCUMENT_TITLES, documentHead } from "#/lib/documentTitle";
import {
  myEntitlementsQueryOptions,
  playerProfileQueryOptions,
  profileActivityQueryOptions,
} from "#/lib/queries";
import { loadProfilePage } from "#/server/loadProfilePage";
import { requireProfileAuth } from "#/server/requireProfileAuth";

import { AchievementsCard, AchievementsCardSkeleton } from "./-profile.achievements";
import { ProfileActivityCard, ProfileActivityCardSkeleton } from "./-profile.activityGraph";
import { DiscordCard } from "./-profile.discordCard";
import {
  ProfilePageScaffold,
  ProfilePageTitle,
  ProfilePageTitleSkeleton,
} from "./-profile.scaffold";
import { SettingsForm, SettingsCardSkeleton } from "./-profile.settingsForm";
import { StatsCard, StatsCardSkeleton } from "./-profile.statsBody";
import { SubscriptionCard, SubscriptionCardSkeleton } from "./-profile.subscriptionCard";

export const Route = createFileRoute("/profile")({
  head: () => documentHead(DOCUMENT_TITLES.profile),
  beforeLoad: async () => await requireProfileAuth(),
  loader: async ({ context }) => {
    const { userId } = context;
    const { profile, activity, achievements, earnedAchievementCount, entitlements } =
      await loadProfilePage({ data: {} });

    context.queryClient.setQueryData(playerProfileQueryOptions(userId).queryKey, profile);
    context.queryClient.setQueryData(profileActivityQueryOptions(userId).queryKey, activity);
    context.queryClient.setQueryData(myEntitlementsQueryOptions(userId).queryKey, entitlements);

    return { userId, achievements, earnedAchievementCount };
  },
  pendingComponent: ProfilePageLoading,
  component: ProfilePage,
});

function ProfilePageLoading() {
  return (
    <ProfilePageScaffold eyebrow="Manage your profile" title={<ProfilePageTitleSkeleton />}>
      <ProfileActivityCardSkeleton />
      <AchievementsCardSkeleton />
      <StatsCardSkeleton />
      <SettingsCardSkeleton />
      <SubscriptionCardSkeleton />
    </ProfilePageScaffold>
  );
}

function ProfilePage() {
  const { userId, achievements, earnedAchievementCount } = Route.useLoaderData();
  const { data: profile } = useSuspenseQuery(playerProfileQueryOptions(userId));
  const { data: activity } = useSuspenseQuery(profileActivityQueryOptions(userId));

  return (
    <ProfilePageScaffold
      eyebrow="Manage your profile"
      title={
        <ProfilePageTitle
          displayName={profile.displayName}
          goldMedals={profile.goldMedals}
          silverMedals={profile.silverMedals}
          bronzeMedals={profile.bronzeMedals}
        />
      }
    >
      <ProfileActivityCard activity={activity} />
      <AchievementsCard
        achievements={achievements}
        displayName={profile.displayName}
        earnedCount={earnedAchievementCount}
      />
      <StatsCard profile={profile} />
      <SettingsForm userId={userId} />
      <DiscordCard userId={userId} />
      <SubscriptionCard userId={userId} />
    </ProfilePageScaffold>
  );
}
